import type { Op } from 'quill/core';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import * as styles from './ReadonlyOrganizationSummary.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SummaryCollapsibleContent from '../../components/SummaryCollapsibleContent';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import graphStatsIcon from '../../icons/non-prefilled/16x16/graphStats.svg';
import { UIActions } from '../../ui/uiSlice';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';

interface ReadonlyOrganizationSummaryProps {
  summary: Op[] | null;
  onStartEditing: () => void;
  onActivityOpen?: () => void;
}

const ReadonlyOrganizationSummary = ({
  summary,
  onStartEditing,
  onActivityOpen,
}: ReadonlyOrganizationSummaryProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const isMobile = useIsSize(Sizes.SM);

  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === 'ADMIN',
  );
  const isCollapsed = useAppSelector(
    (state) => state.ui.isOrganizationSummaryCollapsed,
  );

  const menuItems: ReactNode[] = [];
  if (onActivityOpen) {
    menuItems.push(
      <MenuItem key="activity" icon={graphStatsIcon} onClick={onActivityOpen}>
        Activity
      </MenuItem>,
    );
  }
  if (isAdmin && !isMobile) {
    if (menuItems.length > 0) {
      menuItems.push(<MenuSeparator key="separator" />);
    }
    menuItems.push(
      <MenuItem key="edit" icon={editIcon} onClick={onStartEditing}>
        Edit description
      </MenuItem>,
    );
  }

  return (
    <div className={styles.container}>
      {summary ? (
        <SummaryCollapsibleContent
          collapsed={isCollapsed}
          onCollapsedChange={(newCollapsed) => {
            dispatch(
              UIActions.toggleOrganizationSummaryCollapsed(newCollapsed),
            );
          }}
          maxLines={7}
          typography="post"
          content={summary}
        />
      ) : (
        <div className={styles.header}>
          <h1>Home</h1>
        </div>
      )}
      {menuItems.length > 0 && (
        <div className={styles.overflowMenu}>
          <OverflowMenu
            open={isOverflowMenuOpen}
            onOpenChange={setIsOverflowMenuOpen}
            applyNegativeMargin={false}
          >
            {menuItems}
          </OverflowMenu>
        </div>
      )}
    </div>
  );
};

export default ReadonlyOrganizationSummary;
